import type { AppType } from '@app/api';
import { hc } from 'hono/client';

/**
 * Typed API client. Per the architecture plan §"Frontend type safety:
 * `hono/client`", the SPA consumes the API via `hc<AppType>(...)` so
 * paths, query params, request bodies, and responses are typed
 * end-to-end with no codegen step.
 *
 * Currently `AppType` only includes the routes that have been migrated
 * to the new typed-route pattern (today: business_functions). The
 * older fetch wrappers in `../api.ts` continue to handle endpoints
 * that haven't migrated yet.
 *
 * `credentials: 'include'` is required for the session-cookie path
 * (browser → api.* host); the bearer-token path used by Claude Code
 * MCP clients is irrelevant here.
 */

const API_URL = import.meta.env.VITE_API_URL ?? 'https://api.technicalaccounting.net';

export const apiClient = hc<AppType>(API_URL, {
  init: { credentials: 'include' },
});

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly code?: string,
    public readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface ApiErrorBody {
  error?: string;
  message?: string;
  details?: Record<string, unknown>;
}

/**
 * Convert a Response from a hono/client call into a typed body, or
 * throw `ApiError` for non-2xx. View-model hooks wrap their fetches
 * with this so React Query sees throws (not silent failures).
 */
export async function unwrap<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as ApiErrorBody;
    throw new ApiError(
      res.status,
      body.message ?? res.statusText,
      body.error,
      body.details,
    );
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}
